import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { v4 as uuidv4 } from 'uuid';
import { ADD_TO_CART, IS_ADDING_TO_CART, PRODUCT_STATE_CHANGE, QUICKSHOP_ITEM_READY, } from '../../../events';
import { getSite } from '../../../utils';
import AddAlertButton from './buttons/AddAlertButton';
import AddToBasketButton from './buttons/AddToBasketButton';
import AvailableSoonButton from './buttons/AvailableSoonButton';
import LastMinuteShoppingButton from './buttons/LastMinuteShoppingButton';
import { clearLastCall, renderLastCall } from './messages/LastCall';
import { isDesktopDisplay } from '../../mobile/check';
/**
 * Each button is linked with product hash, each time a new variant or item is selected, an event is dispatched with the selected hash.
 * Buttons are listening to this event to update accordingly.
 */
export const ProductCta = ({ onlyBasketCta = false, allowAddedStatus, product, inDialogContext, isQuickshopContext = false }) => {
    const size = !inDialogContext && window.location.hash.replace('#size-', '');
    // Local scoped uniq id to target buttons with custom events
    const [idButton] = useState(uuidv4());
    const [selectedVariant, setSelectedVariant] = useState(document.body.dataset.variant && !inDialogContext
        ? product.variants.find((variant) => variant.id === Number(document.body.dataset.variant))
        : product.selectedVariant);
    let defaultSelectedItem = selectedVariant.selectedItem;
    if (size) {
        const selectedItem = selectedVariant.items.find((item) => item.size === size);
        if (selectedItem) {
            defaultSelectedItem = selectedItem;
        }
    }
    const [selectedItem, setSelectedItem] = useState(defaultSelectedItem);
    const [addedToCart, setAddedToCart] = useState(false);
    const [isLoadingId, setIsLoadingId] = useState(undefined);
    const selectedVariantRef = useRef(selectedVariant);
    selectedVariantRef.current = selectedVariant;
    // Desktop overlay quickshop: fires item-ready on every size pick, including re-select after radio reset.
    const [sizeSelectionCount, setSizeSelectionCount] = useState(0);
    // Listen to product added to cart
    useEffect(() => {
        const onProductAddedToCart = (e) => {
            if (allowAddedStatus && e.detail?.success && e.detail.ean === selectedItem.ean) {
                setAddedToCart(true);
            }
        };
        document.addEventListener(ADD_TO_CART, onProductAddedToCart);
        return () => {
            document.removeEventListener(ADD_TO_CART, onProductAddedToCart);
        };
    }, [selectedItem]);
    useEffect(() => {
        const isAddingProductToCart = (e) => {
            if (e.detail.id.includes(idButton)) {
                setIsLoadingId(e.detail.loading ? e.detail.id : undefined);
            }
        };
        document.addEventListener(IS_ADDING_TO_CART, isAddingProductToCart);
        return () => {
            document.removeEventListener(IS_ADDING_TO_CART, isAddingProductToCart);
        };
    }, [selectedItem]);
    // Listen to product state change (new variant or new item selected)
    useEffect(() => {
        if (inDialogContext) {
            const onProductStateChange = (e) => {
                const data = e.detail;
                if (data.productHash !== product.hash)
                    return;
                let selectionUpdated = false;
                if (data.currentVariantHash) {
                    const variant = product.variants.find((productVariant) => productVariant.hash === data.currentVariantHash);
                    if (variant) {
                        setSelectedVariant(variant);
                        setSelectedItem(data.currentItemHash
                            ? variant.items.find((item) => item.hash === data.currentItemHash) ?? variant.selectedItem
                            : variant.selectedItem);
                        selectionUpdated = true;
                    }
                }
                else if (data.currentItemHash) {
                    const variant = selectedVariantRef.current;
                    const newSelectedItem = variant?.items.find((item) => item.hash === data.currentItemHash);
                    if (newSelectedItem) {
                        setSelectedItem(newSelectedItem);
                        selectionUpdated = true;
                    }
                }
                if (isQuickshopContext && selectionUpdated) {
                    setSizeSelectionCount((count) => count + 1);
                }
            };
            document.addEventListener(PRODUCT_STATE_CHANGE, onProductStateChange);
            return () => {
                document.removeEventListener(PRODUCT_STATE_CHANGE, onProductStateChange);
            };
        }
    }, [inDialogContext, isQuickshopContext, product.hash]);
    useEffect(() => {
        if (isQuickshopContext && selectedItem.hash && sizeSelectionCount > 0) {
            document.dispatchEvent(new CustomEvent(QUICKSHOP_ITEM_READY, { detail: { productHash: product.hash } }));
        }
    }, [isQuickshopContext, product.hash, selectedItem, sizeSelectionCount]);
    // Listen to size change (product page)
    useEffect(() => {
        if (!inDialogContext) {
            const onPopState = () => {
                const hashSize = decodeURIComponent(window.location.hash.replace('#size-', ''));
                if (selectedItem && hashSize !== selectedItem.size) {
                    setSelectedItem(selectedVariant.items.find((item) => item.size === hashSize));
                }
            };
            window.addEventListener('popstate', onPopState);
            return () => {
                window.removeEventListener('popstate', onPopState);
            };
        }
    }, [inDialogContext, product.hash, selectedItem, selectedVariant]);
    // Last call is PDP-only (specs: not in quickshop). Slot [data-product-last-call] sits outside this React root.
    const isPdpMainCta = !inDialogContext && !onlyBasketCta;
    useEffect(() => {
        if (!isPdpMainCta) {
            return;
        }
        const shouldShowLastCall = selectedItem.isLastCall && selectedItem.available && !selectedItem.inOtherBasket;
        if (shouldShowLastCall) {
            renderLastCall();
        }
        else {
            clearLastCall();
        }
        return () => clearLastCall();
    }, [isPdpMainCta, selectedItem]);
    if (!selectedVariant) {
        return null;
    }
    const siteCode = getSite();
    const isPreorder = selectedVariant.preorder;
    const isAvailableSoon = selectedVariant.availableSoon;
    const isCustomisation = !!selectedVariant.customisation;
    const isAvailable = selectedItem.available;
    const isInOtherBasket = selectedItem.inOtherBasket;
    const lastMinuteShoppingAvailable = selectedItem.lastMinuteShoppingAvailable && siteCode?.startsWith('fr');
    const isCrossSellPopin = selectedVariant.isCrossSellPopin;
    const isCrossSellProductPopin = selectedVariant.isCrossSellProductPopin;
    const selectedVariantTracking = selectedVariant.tracking;
    const tracking = {
        brand: selectedVariantTracking.brand,
        category: selectedVariantTracking.category,
        ean: selectedItem.ean,
        name: selectedVariantTracking.name,
        price: selectedVariantTracking.productPrice,
        variantColor: selectedVariantTracking.variantColor,
        content_ids: selectedVariantTracking.legacyId,
        productUUID: selectedVariantTracking.productUUID,
        sizeUUID: selectedItem.uuid,
        legacyId: selectedVariantTracking.legacyId,
        productBrand: selectedVariantTracking.productBrand,
        productCategory: selectedVariantTracking.productCategory,
        productEAN: selectedVariantTracking.productEAN,
        productID: selectedVariantTracking.productID,
        productName: selectedVariantTracking.productName,
        productPrice: selectedVariantTracking.productPrice,
        productStatus: selectedVariantTracking.productStatus,
        productURL: selectedVariantTracking.productURL,
        productVariant: selectedVariantTracking.productVariant,
        fsColor: selectedVariantTracking.fsColor,
        color: selectedVariantTracking.color,
        collection: selectedVariantTracking.collection,
    };
    // Quickshop: needs an actionable cart CTA (available or preorder) — not LMS-only, not inOtherBasket-only
    const isUnavailableInQuickshop = isQuickshopContext
        ? isAvailableSoon || (!isAvailable && !isPreorder)
        : isAvailableSoon ||
            (!isAvailable && !isInOtherBasket && !lastMinuteShoppingAvailable && !isPreorder);
    if (isQuickshopContext && isUnavailableInQuickshop) {
        return null;
    }
    // Quickshop: LMS on mobile only alongside an available cart CTA; never on desktop (onlyBasketCta)
    const showLastMinuteShopping = lastMinuteShoppingAvailable && (!isQuickshopContext || isAvailable);
    let mainCta = _jsx(AddAlertButton, { soon: isAvailableSoon, tracking: tracking });
    if (isAvailableSoon) {
        mainCta = _jsx(AvailableSoonButton, {});
    }
    else if (isAvailable || isInOtherBasket) {
        mainCta = (_jsx(AddToBasketButton, { isAvailable: isAvailable, isPreorder: isPreorder, isInOtherBasket: isInOtherBasket, isCustomisation: isCustomisation, preorderDate: selectedVariant.preorderDate, preorderDateEnd: selectedVariant?.preorderDateEnd?.date
                ? new Date(selectedVariant.preorderDateEnd.date + selectedVariant.preorderDateEnd.timezone)
                : null, tracking: tracking, withTooltip: !onlyBasketCta, linkDesktopOverride: isQuickshopContext, popinCrossSellPath: (isCrossSellPopin || isCrossSellProductPopin) && selectedVariant.link, popinCrossSellProductEnabled: isCrossSellProductPopin, ean: selectedItem.ean, addedToCart: addedToCart, isLoading: isLoadingId === idButton, id: idButton }));
    }
    if (onlyBasketCta || addedToCart) {
        return mainCta;
    }
    const lastMinuteIdButton = `${idButton}-lms`;
    const isLastMinuteLoading = isLoadingId === lastMinuteIdButton;
    return (_jsx("div", { children: !isAvailable && !isPreorder && !isInOtherBasket ? (_jsxs(_Fragment, { children: [showLastMinuteShopping && (_jsx(LastMinuteShoppingButton, { id: lastMinuteIdButton, className: "u-mb-sm", tracking: tracking, isLoading: isLastMinuteLoading })), mainCta] })) : (_jsxs(_Fragment, { children: [mainCta, showLastMinuteShopping && (_jsx(LastMinuteShoppingButton, { id: lastMinuteIdButton, className: "u-mt-sm", tracking: tracking, isLoading: isLastMinuteLoading }))] })) }));
};
let ctaReactRoots = [];
// clear to avoid unlinked instances in the wild and updates conflicts
export const clearCtas = () => {
    clearLastCall();
    ctaReactRoots.forEach((root) => {
        try {
            root.unmount();
        }
        catch (error) {
            console.warn('Error while unmounting root:', error);
        }
    });
    ctaReactRoots = [];
};
const parseProductFromContainer = (ctaContainer) => {
    if (!ctaContainer.dataset.product) {
        return window.product ?? null;
    }
    try {
        return JSON.parse(ctaContainer.dataset.product);
    }
    catch (error) {
        console.warn('Invalid data-product JSON on CTA container, skipping:', ctaContainer, error);
        return null;
    }
};
export const renderProductCta = (config) => {
    const { container = document, inDialogContext = false } = config || {};
    if (container === document) {
        clearCtas();
    }
    container.querySelectorAll('[data-product-cta]').forEach((ctaContainer) => {
        const product = parseProductFromContainer(ctaContainer);
        if (!product)
            return;
        const isQuickshopPanel = !!ctaContainer.closest('[data-cms-quickshop-panel]');
        // Desktop quickshop shows only the basket button (no LMS, no extras)
        const onlyBasketCta = ctaContainer.hasAttribute('data-only-basket-cta') || (isQuickshopPanel && isDesktopDisplay());
        const allowAddedStatus = ctaContainer.hasAttribute('data-allow-added-status');
        // Prevent browser auto-translation (mostly Android Chrome / Samsung Internet) from wrapping this
        // React-owned subtree in <font> tags. The CTA text swaps on every click (label -> loading -> "added to
        // cart") and React would then removeChild() a node the translator already replaced, throwing
        // "NotFoundError: Failed to execute 'removeChild' on 'Node'" and making the button disappear.
        ctaContainer.setAttribute('translate', 'no');
        ctaContainer.classList.add('notranslate');
        const root = createRoot(ctaContainer);
        root.render(_jsx(ProductCta, { onlyBasketCta: onlyBasketCta, allowAddedStatus: allowAddedStatus, product: product, inDialogContext: inDialogContext || isQuickshopPanel, isQuickshopContext: isQuickshopPanel }));
        ctaReactRoots.push(root);
    });
};
